import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Number of unresolved, unmuted admin error incidents seen in the last 24h.
 * Used to render a pulsing red dot on the admin nav entry.
 */
export function useAdminUnresolvedIncidentCount() {
  const { user, isAdmin } = useAuth();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["admin-unresolved-incidents"],
    enabled: !!user && !!isAdmin,
    staleTime: 30_000,
    queryFn: async () => {
      const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const { count, error } = await supabase
        .from("admin_error_incidents")
        .select("id", { count: "exact", head: true })
        .eq("is_muted", false)
        .eq("is_resolved", false)
        .gte("last_seen_at", since);
      if (error) throw error;
      return count ?? 0;
    },
  });

  // Keep fresh on realtime activity even if the alerts hook isn't mounted
  useEffect(() => {
    if (!user || !isAdmin) return;
    const channel = supabase
      .channel("admin-incidents-badge")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "admin_error_incidents" },
        () => {
          qc.invalidateQueries({ queryKey: ["admin-unresolved-incidents"] });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, isAdmin, qc]);

  return query.data ?? 0;
}